import { formatearMoneda, type CitaVista } from '@barbones/shared';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { toast } from 'sonner';
import { api } from '@/api/cliente';
import { Button } from '@/components/ui/button';
import { Campo, Select } from '@/components/ui/form';
import { Dialogo } from '@/components/ui/overlay';
import { avisarError } from '@/lib/avisos';
import { useServicios } from './datos';

interface Props {
  citaId: string;
  /** Servicio que se eligió por error. */
  anterior: { id: string; nombre: string };
  /** Servicios que ya tiene la cita: no se ofrecen de nuevo. */
  serviciosCita: string[];
  onCerrar: () => void;
}

/** Corrige un servicio mientras se atiende: el carrito toma el precio del nuevo y la cita queda con él. */
export function CambiarServicioDialog({ citaId, anterior, serviciosCita, onCerrar }: Props) {
  const servicios = useServicios();
  const qc = useQueryClient();
  const [nuevoId, setNuevoId] = useState('');
  const opciones = (servicios.data ?? []).filter((s) => s.activo && s.precio > 0 && !serviciosCita.includes(s.id));
  const nuevo = opciones.find((s) => s.id === nuevoId);

  const cambiar = useMutation({
    mutationFn: () =>
      api<CitaVista>(`/atencion/por-cita/${citaId}/cambiar-servicio`, { method: 'POST', body: { servicioAnteriorId: anterior.id, servicioNuevoId: nuevoId } }),
    onSuccess: () => {
      toast.success(`Servicio cambiado a ${nuevo?.nombre ?? 'el nuevo servicio'}`);
      void qc.invalidateQueries({ queryKey: ['agenda'] });
      void qc.invalidateQueries({ queryKey: ['ventas'] });
      onCerrar();
    },
    onError: (e) => avisarError(e),
  });

  return (
    <Dialogo
      abierto
      ancho="sm"
      onCambiarAbierto={(v) => !v && !cambiar.isPending && onCerrar()}
      titulo="Cambiar servicio"
      descripcion={`Reemplaza «${anterior.nombre}» en la cita y en el carrito.`}
      pie={
        <>
          <Button variante="secundario" onClick={onCerrar} disabled={cambiar.isPending}>
            Volver
          </Button>
          <Button onClick={() => cambiar.mutate()} cargando={cambiar.isPending} disabled={!nuevo}>
            Cambiar servicio
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-3">
        <Campo etiqueta="Servicio correcto" requerido>
          {(p) => (
            <Select {...p} value={nuevoId} onChange={(e) => setNuevoId(e.target.value)} autoFocus>
              <option value="">Seleccionar servicio</option>
              {opciones.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.nombre} · {formatearMoneda(s.precio)}
                </option>
              ))}
            </Select>
          )}
        </Campo>
        {nuevo && <p className="cifras text-[13px] font-medium">Nuevo total del servicio: {formatearMoneda(nuevo.precio)}</p>}
        <p className="text-[12.5px] text-foreground-muted">Si la venta tiene promoción o descuento, se recalcula con el precio nuevo.</p>
      </div>
    </Dialogo>
  );
}
